import { Injectable } from '@angular/core';
import {
    ReportType,
    ReportTypeType,
    ExternalAssessmentStates,
    ScoreInfoType,
    SurveyUserTypes
} from '../constants-types/Enums';
import {
    CognitiveInformation,
    BehavioralInformation
} from '../core-data-model/AssessmentInformation.interface';
import { AssessmentUser } from '../core-data-model/AssessmentUser.interface';
import { BehavioralFactorRanges } from '../core-data-model/BehavioralFactorRanges.model';
import { BehavioralReportConfiguration } from '../core-data-model/BehavioralReportConfiguration.model';
import { BehavioralReportData } from '../core-data-model/BehavioralReportData.model';
import { CognitiveAssessment } from '../core-data-model/CognitiveAssessment.model';
import { CognitiveReportData } from '../core-data-model/CognitiveReportData.model';
import { MinMax } from '../core-data-model/Common.model';
import {
    Job,
    PersonFit,
    JobMatchingPerson
} from '../core-data-model/Job.model';
import { FullPattern } from '../core-data-model/Pattern.model';
import { PatternInsights } from '../core-data-model/PatternInsights.interface';
import { Person, JobFit, PersonUpdate } from '../core-data-model/Person.model';
import {
    PersonAssessmentConsentDetailsServer,
    CogReportServer,
    BAReportServer,
    PersonServerPost,
    PersonServerResponse
} from '../core-data-model/PersonApi.serverinterface';
import { PersonAssessmentConsent } from '../core-data-model/PersonAssessmentConsent.model';
import { ReferencePatternsInformation } from '../core-data-model/ReferencePatternType.model';
import { ReportEmailConfiguration } from '../core-data-model/ReportEmailConfiguration.model';
import { PeopleAndJobsPersonServer } from '../core-data-model/SearchApi.serverinterface';
import { FoldersDataService } from '../core-services/Folders.dataservice';
import { PatternTransformer } from '../core-services/Pattern.datatransform';
import { PersonApiUpdate } from '../core-services/PersonApi.connect';
import { Util } from '../core-utilities/Util';
import { BehavioralAssessmentFactory } from '../object-factory/BehavioralAssessment.factory';
import { InitDataService } from '../system-services/InitData/InitData.service';

@Injectable({
    providedIn: 'root'
})
export class PersonApiTransformer {
    constructor(
        private util: Util,
        private baFactory: BehavioralAssessmentFactory,
        private initData: InitDataService,
        private foldersService: FoldersDataService
    ) {}

    // static to allow image service access
    static transformToBAReport(
        baReportJSON: BAReportServer
    ): BehavioralReportData {
        const baReport = new BehavioralReportData({
            piLanguageLocale: baReportJSON.piLanguageLocale,
            surveyTakerName: baReportJSON.surveyTakerName,
            firstName: baReportJSON.firstName,
            lastName: baReportJSON.lastName,
            dateTaken: baReportJSON.dateTaken,
            reportSummary: baReportJSON.reportSummary,
            strongestBehaviorIntro: baReportJSON.strongestBehaviorIntro,
            strongestBehaviorHeader: baReportJSON.strongestBehaviorHeader,
            strongestBehaviorContents: baReportJSON.strongestBehaviorContents,
            influencingTexts: baReportJSON.influencingTexts,
            managementTexts: baReportJSON.managementTexts,
            sellingTexts: baReportJSON.sellingTexts,
            managementStyleIntro: baReportJSON.managementStyleIntro,
            influencingStyleIntro: baReportJSON.influencingStyleIntro,
            sellingStyleIntro: baReportJSON.sellingStyleIntro,
            managementStrategyTexts: baReportJSON.managementStrategyTexts,
            managementStrategyIntro: baReportJSON.managementStrategyIntro,
            gender: baReportJSON.gender,
            isManagementStyle: baReportJSON.isManagementStyle,
            isSellingStyle: baReportJSON.isSellingStyle,
            isInfluencingStyle: baReportJSON.isInfluencingStyle,
            createdByUserId: baReportJSON.createdByUserId,
            referencePattern: ReferencePatternsInformation.find(
                (refPattern) =>
                    refPattern.id === baReportJSON.referencePatternNumber
            ),
            referencePatternName: baReportJSON.referencePatternName,
            referencePatternDescription:
                baReportJSON.referencePatternDescription,
            // patternData details
            patternDataForReportGen: new FullPattern({
                self: {
                    A: baReportJSON.patternDataForReportGen.self.a,
                    B: baReportJSON.patternDataForReportGen.self.b,
                    C: baReportJSON.patternDataForReportGen.self.c,
                    D: baReportJSON.patternDataForReportGen.self.d,
                    E: baReportJSON.patternDataForReportGen.self.e,
                    M: baReportJSON.patternDataForReportGen.self.m,
                    type: ScoreInfoType.Self
                },
                selfConcept: {
                    A: baReportJSON.patternDataForReportGen.selfConcept.a,
                    B: baReportJSON.patternDataForReportGen.selfConcept.b,
                    C: baReportJSON.patternDataForReportGen.selfConcept.c,
                    D: baReportJSON.patternDataForReportGen.selfConcept.d,
                    E: baReportJSON.patternDataForReportGen.selfConcept.e,
                    M: baReportJSON.patternDataForReportGen.selfConcept.m,
                    type: ScoreInfoType.SelfConcept
                },
                synthesis: {
                    A: baReportJSON.patternDataForReportGen.synthesis.a,
                    B: baReportJSON.patternDataForReportGen.synthesis.b,
                    C: baReportJSON.patternDataForReportGen.synthesis.c,
                    D: baReportJSON.patternDataForReportGen.synthesis.d,
                    E: baReportJSON.patternDataForReportGen.synthesis.e,
                    M: baReportJSON.patternDataForReportGen.synthesis.m,
                    type: ScoreInfoType.Synthesis
                },
                scoreId: baReportJSON.patternDataForReportGen.scoreId
            })
        });

        return baReport;
    }

    public transformPerson(
        searchResultJSON: PeopleAndJobsPersonServer,
        jobFit: JobFit = null
    ): Person {
        const userJSON = searchResultJSON.assessmentUser;
        return new Person({
            lastSent: searchResultJSON.assessmentInformation
                ? this.util.newDate(
                      searchResultJSON.assessmentInformation
                          .latestAssessmentEmailSentDate
                  )
                : undefined,
            lastCompleted: searchResultJSON.assessmentInformation
                ? this.util.newDate(
                      searchResultJSON.assessmentInformation
                          .latestAssessmentCompletedDate
                  )
                : undefined,
            lastUpdated: this.util.newDate(searchResultJSON.sortDate),
            id: userJSON.assessmentUserId,
            firstName: userJSON.firstName,
            middleName: userJSON.middleName,
            lastName: userJSON.lastName,
            email: userJSON.email,
            isManager: searchResultJSON.isManager,
            jobFit: this.calculateJobFit(jobFit, userJSON),
            job: searchResultJSON.jobInformation
                ? new Job({
                      id: searchResultJSON.jobInformation.id,
                      title: searchResultJSON.jobInformation.name
                  })
                : undefined,
            type: SurveyUserTypes[
                SurveyUserTypes[
                    searchResultJSON.assessmentUser.personType as any
                ] as any
            ] as any,
            folder: this.foldersService.getFolder(searchResultJSON.folderId),
            behavioralAssessments:
                searchResultJSON.assessmentInformation &&
                searchResultJSON.assessmentInformation
                    .behavioralAssessmentInformation
                    ? this.transformBehavioralAssessments(
                          searchResultJSON.assessmentInformation
                              .behavioralAssessmentInformation
                      )
                    : [],
            userId: userJSON.userId,
            cognitiveAssessments:
                searchResultJSON.assessmentInformation &&
                searchResultJSON.assessmentInformation.cognitiveInformation
                    ? [
                          this.transformCognitiveAssessment(
                              searchResultJSON.assessmentInformation
                                  .cognitiveInformation
                          )
                      ]
                    : [],
            referencePattern: searchResultJSON.referencePatternInformation
                ? ReferencePatternsInformation.find(
                      (refPattern) =>
                          refPattern.id ===
                          searchResultJSON.referencePatternInformation
                              .patternNumber
                  )
                : null
        });
    }

    transformToServerReportConfig(
        reportConfig: BehavioralReportConfiguration,
        reportType: ReportTypeType,
        peopleToReportOn: string[] | { personId: string; gender: string }[],
        reportEmailConfig: ReportEmailConfiguration,
        language: string
    ) {
        // TODO: Remove the Temporory Hack when the API's support this
        if (reportEmailConfig.shouldSendToMyself) {
            reportEmailConfig.othersEmailList.push(this.initData.user.email);
        }
        const resultConfig: any = {
            personsToReportOn: peopleToReportOn,
            emailReportToAssessmentTaker:
                reportEmailConfig.shouldSendToAssessmentTaker,
            emailReportsToAnalyst: false,
            othersToEmail: !reportEmailConfig.othersEmailList
                ? ''
                : reportEmailConfig.othersEmailList.length > 1
                  ? reportEmailConfig.othersEmailList.join(',')
                  : reportEmailConfig.othersEmailList[0],
            emailSubject: reportEmailConfig.subject,
            emailMessageBody: reportEmailConfig.message,
            reportLanguage: language
        };

        if (reportType === ReportType.PiReport) {
            resultConfig.reportLanguage = reportConfig.languageLocale;
            resultConfig.isManagementStyle = reportConfig.isManagement;
            resultConfig.isSellingStyle = reportConfig.isSelling;
            resultConfig.isInfluencingStyle = reportConfig.isInfluencing;
        }
        return resultConfig;
    }

    transformToPersonAssessmentConsent(
        serverassessmentConsent: PersonAssessmentConsentDetailsServer
    ): PersonAssessmentConsent {
        const personAssessmentConsent = new PersonAssessmentConsent();
        personAssessmentConsent.id = serverassessmentConsent.assessmentId;
        personAssessmentConsent.consent = serverassessmentConsent.consentText;
        return personAssessmentConsent;
    }

    transformJobMatchesToJobs(matchResponse: any): JobMatchingPerson[] {
        return matchResponse.matchingJobsByMatchScore.map((matchJob: any) => {
            const searchResult = new JobMatchingPerson({
                id: matchJob.jobId,
                title: matchJob.jobName,
                personFit: new PersonFit({
                    overallFitScore: matchJob.combinedJobFitScore,
                    behavioralFitScore: matchJob.behavioralJobFitScore,
                    cognitiveFitScore: matchJob.cognitiveJobFitScore
                }),
                behavioralRanges: new BehavioralFactorRanges({
                    A: new MinMax({
                        min: matchJob.behavioralRanges.A.min,
                        max: matchJob.behavioralRanges.A.max
                    }),
                    B: new MinMax({
                        min: matchJob.behavioralRanges.B.min,
                        max: matchJob.behavioralRanges.B.max
                    }),
                    C: new MinMax({
                        min: matchJob.behavioralRanges.C.min,
                        max: matchJob.behavioralRanges.C.max
                    }),
                    D: new MinMax({
                        min: matchJob.behavioralRanges.D.min,
                        max: matchJob.behavioralRanges.D.max
                    })
                }),
                cognitiveTarget: matchJob.cogJobScore,
                createdByName:
                    matchJob.jobCreatorFirstName +
                    ' ' +
                    matchJob.jobCreatorLastName,
                createdDate: matchJob.jobCreatedDate
            });
            return searchResult;
        });
    }

    prepareReport(
        personDetails: Person,
        patternInsights: PatternInsights,
        behavioralShowJobMatch: boolean
    ) {
        return {
            name: personDetails.fullName(),
            jobName: personDetails.job ? personDetails.job.title : '',
            referencePatternId: personDetails.referencePattern.id,
            pattern: personDetails.originalAssessment.pattern, // do I need to break this down further of will JSON serializer do al I need
            jobRanges: personDetails.job
                ? personDetails.job.behavioralRanges
                : null,
            patternInsights: patternInsights,
            behavioralShowJobMatch
        };
    }

    transformToCogReport(cogReportJSON: CogReportServer): CognitiveReportData {
        const cogReport = new CognitiveReportData({
            jobTitle: cogReportJSON.jobTitle,
            firstName: cogReportJSON.firstName,
            lastName: cogReportJSON.lastName,
            personScore: cogReportJSON.personScore,
            jobScore: cogReportJSON.jobScore,
            matchScore: cogReportJSON.matchScore,
            assessmentDate: cogReportJSON.assessmentDate,
            totalAttempted: cogReportJSON.totalAttempted,
            totalCorrect: cogReportJSON.totalCorrect,
            subScoreVAttempted: cogReportJSON.subScoreVAttempted,
            subScoreVCorrect: cogReportJSON.subScoreVCorrect,
            subScoreNAttempted: cogReportJSON.subScoreNAttempted,
            subScoreNCorrect: cogReportJSON.subScoreNCorrect,
            subScoreRAttempted: cogReportJSON.subScoreRAttempted,
            subScoreRCorrect: cogReportJSON.subScoreRCorrect,
            showSensitiveInformation: cogReportJSON.showSensitiveInformation
        });

        return cogReport;
    }

    transformToServerCogReportConfig(
        peopleToReportOn: string[],
        reportEmailConfig: ReportEmailConfiguration,
        languageLocale: string,
        showSensitiveInfo: boolean
    ) {
        if (reportEmailConfig.shouldSendToMyself) {
            reportEmailConfig.othersEmailList.push(this.initData.user.email);
        }
        const resultConfig = {
            personsToReportOn: peopleToReportOn,
            emailReportToAssessmentTaker:
                reportEmailConfig.shouldSendToAssessmentTaker,
            emailReportsToAnalyst: false,
            emailTo: !reportEmailConfig.othersEmailList
                ? ''
                : reportEmailConfig.othersEmailList.length > 1
                  ? reportEmailConfig.othersEmailList.join(',')
                  : reportEmailConfig.othersEmailList[0],
            emailSubject: reportEmailConfig.subject,
            emailMessageBody: reportEmailConfig.message,
            reportLanguage: languageLocale,
            showSensitiveInformation: showSensitiveInfo
        };

        return resultConfig;
    }

    transformToBAReport(baReportJSON: BAReportServer): BehavioralReportData {
        return PersonApiTransformer.transformToBAReport(baReportJSON);
    }

    transformPersonToPost(person: PersonUpdate): PersonServerPost {
        return {
            firstName: person.firstName,
            middleName: person.middleName,
            lastName: person.lastName,
            email: person.email,
            personType: person.type.toString(),
            folderId: person.folder.id,
            associatedJobId:
                person.job && person.job !== 'RemoveJob' ? person.job.id : ''
        };
    }

    // The Server really doesn't return consistent objects an d won't return
    // a job. So this approach takes what the server returns, but also uses
    // stuff that I already know
    transformPersonFromPost(
        serverData: PersonServerResponse,
        originalPerson: PersonUpdate
    ): Person {
        return new Person(
            Object.assign({}, originalPerson, {
                // Handle janky typing in PersonUpdate
                job:
                    typeof originalPerson.job === 'string'
                        ? null
                        : originalPerson.job,
                id: serverData.personId,
                firstName: serverData.firstName,
                middleName: serverData.middleName,
                lastName: serverData.lastName,
                gender: serverData.gender, // Does this work?
                email: serverData.email,
                folder: this.foldersService.getFolder(serverData.folderId),
                type: SurveyUserTypes[serverData.personType]
            })
        );
    }

    transformPatch(update: PersonUpdate) {
        const apiBody: PersonApiUpdate = {
            firstName: update.firstName,
            middleName: update.middleName,
            lastName: update.lastName,
            email: update.email,
            folderPath: update.folder ? update.folder.folderPath : undefined,
            type: update.type
        };
        if (update.job === 'RemoveJob') {
            apiBody.removeJobAssociation = true;
        } else if (update.job) {
            apiBody.associatedPositionTitle = update.job.title;
            apiBody.associatedJobId = update.job.id;
        }
        return apiBody;
    }

    private calculateJobFit(jobFit: JobFit, userJSON: AssessmentUser) {
        if (jobFit) return jobFit;
        return userJSON.impression !== undefined
            ? new JobFit({
                  personImpression: userJSON.impression,
                  overallFitScore: userJSON.combinedJobFitScore,
                  behavioralFitScore: userJSON.behavioralJobFitScore,
                  cognitiveFitScore: userJSON.cognitiveJobFitScore
              })
            : undefined;
    }

    private transformBehavioralAssessments(
        behavioralAssessmentJSON: BehavioralInformation
    ) {
        const retval = [];

        /* Create 'Last' */
        retval.push(
            this.baFactory.new_BehavioralAssessment({
                completedDate: this.util.newDate(
                    behavioralAssessmentJSON.completedDateTime
                ),
                sentDate: this.util.newDate(behavioralAssessmentJSON.sentDate),
                id: behavioralAssessmentJSON.id,
                administeredBy: behavioralAssessmentJSON.administeredBy,
                administeredById: behavioralAssessmentJSON.administeredById,
                assessmentState: behavioralAssessmentJSON.assessmentState,
                scoreId: behavioralAssessmentJSON.scoreId,
                pattern:
                    behavioralAssessmentJSON.originalId &&
                    behavioralAssessmentJSON.originalId ===
                        behavioralAssessmentJSON.id
                        ? PatternTransformer.transformToFullPattern(
                              behavioralAssessmentJSON
                          )
                        : undefined
            })
        );

        /* Create 'Original' - this should have a pattern */
        if (
            behavioralAssessmentJSON.originalId &&
            behavioralAssessmentJSON.originalId !== behavioralAssessmentJSON.id
        ) {
            retval.push(
                this.baFactory.new_BehavioralAssessment({
                    // Hey, at the time of coding this, the BSID returned by the index
                    // was for the latest. I decided to pretend that this BSID was for the
                    // original to A) work seamlessly when the index is fixed and to
                    // B) make it work like it was already working.
                    // The index bug was captured in PI2-4779
                    scoreId: behavioralAssessmentJSON.scoreId,
                    id: behavioralAssessmentJSON.originalId,
                    pattern: PatternTransformer.transformToFullPattern(
                        behavioralAssessmentJSON
                    )
                })
            );
        }

        /* Create 'Pending' - this should have no pattern */
        if (
            behavioralAssessmentJSON.pendingAssessmentId &&
            behavioralAssessmentJSON.pendingAssessmentId !==
                behavioralAssessmentJSON.id
        ) {
            retval.push(
                this.baFactory.new_BehavioralAssessment({
                    id: behavioralAssessmentJSON.pendingAssessmentId,
                    sentDate: this.util.newDate(
                        behavioralAssessmentJSON.pendingAssessmentSentDate
                    ),
                    administeredBy:
                        behavioralAssessmentJSON.pendingAdministeredBy,
                    administeredById:
                        behavioralAssessmentJSON.pendingAdministeredById,
                    /* Below seems like a fairly valid assumption - Enum is a little worrying though */
                    assessmentState: ExternalAssessmentStates.Pending
                })
            );
        }
        return retval;
    }

    private transformCognitiveAssessment(
        cognitiveJSON: CognitiveInformation
    ): CognitiveAssessment {
        return new CognitiveAssessment({
            score: cognitiveJSON.score,
            completedDate: this.util.newDate(
                cognitiveJSON.assessmentCompletedDateTime
            ),
            sentDate: this.util.newDate(cognitiveJSON.assessmentSentDate),
            id: cognitiveJSON.id,
            assessmentState: cognitiveJSON.assessmentState,
            administeredBy: cognitiveJSON.administeredBy,
            administeredById: cognitiveJSON.administeredById
        });
    }
}
